import {
  preferenceKeys,
  readPreference,
  removePreference,
  writePreference,
} from '@core/settings/preferences';
import { SNAPSHOT_METRICS, TELEMETRY_EVENTS, type TelemetryEvent, type TelemetrySnapshot } from './events';

/**
 * Счётчики за сутки — порт `LocalTelemetry.kt`.
 *
 * Наружу уходит не поток событий, а суточный агрегат: «за 10.09.2026 сканирований 3,
 * открытий документов 12». Последовательность действий и точное время устройство
 * не покидают — восстанавливать по переданным данным нечего. См. docs/TELEMETRY.md.
 *
 * **Сбор идёт локально всегда, отправка — только пока включён выключатель.** Цифры
 * лежат на устройстве, и юрист видит их сам в настройках.
 *
 * Хранилище — `localStorage`, а не база. Счётчики к делам отношения не имеют, а база
 * в вебе бывает занята записью документа; терять из-за статистики нечего, и лезть
 * в неё ради двух десятков чисел незачем. В приватном Safari `localStorage` бросает
 * исключение на любом обращении — тогда телеметрия просто ничего не считает и ничего
 * не ломает: всё чтение и запись обёрнуты в `preferences.ts`.
 */
export class LocalTelemetry {
  /** Часы отдельным параметром: тест не должен зависеть от того, какое сегодня число. */
  constructor(private readonly now: () => Date = () => new Date()) {}

  /** Считает событие. Ничего не ждёт и никогда не бросает: экран не зависит от статистики. */
  record(event: TelemetryEvent): void {
    const key = this.keyOf(this.today(), TELEMETRY_EVENTS[event]);
    writePreference(key, String(this.readInt(key) + 1));
  }

  /**
   * Срез состояния на конец суток. Перезаписывает вчерашние значения за то же число:
   * это состояние, а не событие, и складывать его нельзя.
   */
  saveSnapshot(snapshot: TelemetrySnapshot): void {
    const date = this.today();
    for (const [name, value] of Object.entries(snapshot)) {
      writePreference(this.keyOf(date, name), String(Math.max(0, Math.round(value))));
    }
  }

  /**
   * Всё накопленное по суткам: дата → (метрика → значение).
   *
   * Отсюда берёт данные отправка. Отметок «уже отправлено» нет и не нужно: сервер
   * идемпотентен по (install_id, дата, метрика), повтор перезаписывает значение,
   * а не складывает. Очередь, которая не хранит состояния, не умеет разойтись
   * с сервером — нечему.
   */
  allDays(): Record<string, Record<string, number>> {
    const byDate: Record<string, Record<string, number>> = {};

    for (const key of preferenceKeys(DAY_PREFIX)) {
      const [date, metric] = key.slice(DAY_PREFIX.length).split('.');
      if (!metric || !isDate(date)) continue;

      (byDate[date] ??= {})[metric] = this.readInt(key);
    }
    return byDate;
  }

  /** Что накоплено за сутки: метрика → значение. Показывается юристу в настройках. */
  todayValues(): Record<string, number> {
    return this.allDays()[this.today()] ?? {};
  }

  /**
   * Убирает агрегаты старше [KEEP_DAYS] суток: очередь не должна расти бесконечно,
   * пока сервер недоступен. TTL в 30 дней задан в docs/TELEMETRY.md.
   */
  dropOlderThan(edge: string = this.dateBack(KEEP_DAYS)): void {
    for (const key of preferenceKeys(DAY_PREFIX)) {
      const date = key.slice(DAY_PREFIX.length).split('.')[0];
      if (isDate(date) && date < edge) removePreference(key);
    }
  }

  /**
   * Отправка обезличенных цифр. **Включена по умолчанию** — решение владельца
   * ([D-30](docs/DECISIONS.md)): до настроек не дойдёт почти никто, а без цифр нельзя
   * понять, пользуются приложением или нет.
   *
   * Это допустимо ровно потому, что уходит: ни одного названия, ни одного текста,
   * ни одного идентификатора, связываемого с человеком, — только суточные счётчики
   * и срез «сколько дел». Выключатель на видном месте в настройках, и о сборе
   * сказано на первом запуске.
   */
  isSendingEnabled(): boolean {
    return readPreference(SENDING) !== 'no';
  }

  setSendingEnabled(enabled: boolean): void {
    writePreference(SENDING, enabled ? 'yes' : 'no');
  }

  /**
   * Случайный идентификатор установки. Ни адреса, ни почты, ни отпечатка браузера:
   * связать его с человеком нельзя, и сбрасывается он кнопкой в настройках.
   */
  installId(): string {
    const stored = readPreference(INSTALL);
    return stored && UUID.test(stored) ? stored : this.resetInstallId();
  }

  resetInstallId(): string {
    const id = crypto.randomUUID();
    writePreference(INSTALL, id);
    return id;
  }

  /**
   * Когда суточная задача отрабатывала в последний раз; `0` — ни разу.
   *
   * Отметка ставится и после неудачной отправки. Иначе приложение без сети снимало бы
   * срез при каждом открытии, а толку от этого никакого: сервер идемпотентен, и то же
   * самое уйдёт следующим разом.
   */
  lastRunAt(): number {
    return this.readInt(RAN);
  }

  ran(): void {
    writePreference(RAN, String(this.now().getTime()));
  }

  /** Сегодняшнее число устройства в виде `2026-09-10`. */
  today(): string {
    return isoDate(this.now());
  }

  private dateBack(days: number): string {
    const date = this.now();
    date.setDate(date.getDate() - days);
    return isoDate(date);
  }

  private keyOf(date: string, metric: string): string {
    return `${DAY_PREFIX}${date}.${metric}`;
  }

  private readInt(key: string): number {
    const value = Number(readPreference(key));
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  }
}

/** Сколько суток держим агрегаты, пока их не забрал сервер. */
const KEEP_DAYS = 30;

const DAY_PREFIX = 'stats.day.';
const SENDING = 'stats.sending';
const INSTALL = 'stats.install';
const RAN = 'stats.ran';

const UUID = /^[0-9a-fA-F-]{36}$/;

/**
 * Дата **устройства**, а не UTC: юрист работает по своим суткам, и «сегодня» в цифрах
 * должно совпадать с тем, что он видит на экране. `toISOString` сдвинул бы это
 * на часовой пояс.
 */
function isoDate(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const isDate = (value: string | undefined): value is string =>
  value !== undefined && /^\d{4}-\d{2}-\d{2}$/.test(value);

export { SNAPSHOT_METRICS };

/**
 * Общий счётчик приложения.
 *
 * Один на всё приложение и без контекста React: событие считается из обработчика
 * действия, а не из разметки, и тащить ради этого провайдер через все экраны — дороже,
 * чем даёт. Так же устроены и прочие настройки (`preferences.ts`).
 */
export const telemetry = new LocalTelemetry();
